/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { IconMemo } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import getProp from "@insite/mobius/utilities/getProp";
import injectCss from "@insite/mobius/utilities/injectCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import * as React from "react";
import styled, { css } from "styled-components";

const toggleSizes = {
    // xng-change: start - update the default size to match the design
    default: {
        iconSize: 22,
        circleSize: 23,
        height: 31,
        width: 73,
        uncheckedOffset: 44,
    },
    // xng-change: end
    small: {
        iconSize: 18,
        circleSize: 12,
        height: 16,
        width: 36,
        uncheckedOffset: -20,
    },
};

// xng-change: start - Update the margin, width, top and left for the slider to match the design
const Slider = styled.span`
    margin: 0;
    width: auto;
    top: 3px;
    position: absolute;
    left: 4px;
    transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
    display: flex;
    align-items: center;
`;
// xng-change: end

type SliderFrameProps = {
    _sizeVariant: keyof typeof toggleSizes;
    _color?: string;
};
// xng-change: start - update the border-radius, colors, &[aria-disabled="true"], &:not([aria-disabled="true"]), &:focus, &:hover styles
const SliderFrame = styled.div<SliderFrameProps>`
    min-width: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].width}px;
    height: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].height}px;
    border-radius: 14.5px;
    overflow: hidden;
    position: relative;
    display: inline-block;
    box-sizing: border-box;
    background: ${getColor("common.background")};
    border: ${getColor("common.ui10")} 1px solid;
    color: ${getColor("common.ui2")};
    fill: ${getColor("common.ui2")};
    &[aria-checked="false"] ${Slider} {
        left: ${({ _sizeVariant }) => toggleSizes[_sizeVariant].uncheckedOffset}px;
    }
    &[aria-disabled="true"] {
        opacity: 0.33;
    }
    & + label {
        margin-left: 30px;
    }
    &:not([aria-disabled="true"]) {
        &:hover {
            border-color: ${getColor("common.ui3")};
            color: ${getColor("common.ui3")};
            fill: ${getColor("common.ui3")};
        }
    }
    &[aria-checked="true"] {
        background: ${({ _color, theme }) => resolveColor(_color, theme)};
        border: ${({ _color, theme }) => resolveColor(_color, theme)} 1px solid;
        color: ${({ _color, theme }) => getContrastColor(_color, theme)};
        fill: ${({ _color, theme }) => getContrastColor(_color, theme)};
    }
    &[aria-checked="true"]:focus {
        outline: none;
        box-shadow: 0 0 0 2px ${({ _color, theme }) => resolveColor(_color, theme)};
    }

    ${injectCss}
`;
// xng-change: end

type Props = SliderFrameProps & React.InputHTMLAttributes<HTMLInputElement>;

// xng-change - hide the Check icon to match the design
const ToggleInput: React.FC<Props> = ({ _sizeVariant = "default", ...otherProps }) => (
    <SliderFrame _sizeVariant={_sizeVariant} {...otherProps}>
        <Slider>
            <IconMemo
                src="FillCircle"
                size={toggleSizes[_sizeVariant].circleSize}
                color="currentColor"
                css={css`
                    margin: 0;
                `}
            />
        </Slider>
    </SliderFrame>
);

export default ToggleInput;
